/**
 * Chili Piper Affiliate Tracking Utility
 * 
 * Helps set affiliate tracking parameters (platform and click_id) on Chili Piper links
 * for tracking "Book Demo Call" events with Impact
 */

/**
 * Get the irclickid cookie value
 * @returns {string|null} The click ID or null if not found
 */
export function getIrClickId() {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'irclickid') {
      return value;
    }
  }
  return null;
}

/**
 * Add affiliate tracking parameters to a Chili Piper URL
 * @param {string} baseUrl - The base Chili Piper URL
 * @returns {string} The URL with affiliate tracking parameters
 */
export function addAffiliateTrackingToUrl(baseUrl) {
  const clickId = getIrClickId();
  
  if (!clickId) {
    console.log('ChiliPiper affiliate tracking: No irclickid cookie found');
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl);
    
    // Add affiliate tracking parameters
    url.searchParams.set('platform', 'IMPACT');
    url.searchParams.set('click_id', clickId);
    
    console.log('ChiliPiper affiliate tracking: Added tracking parameters', {
      platform: 'IMPACT',
      click_id: clickId
    });
    
    return url.toString();
  } catch (error) {
    console.error('ChiliPiper affiliate tracking: Error parsing URL', error);
    return baseUrl;
  }
}

/**
 * Update Chili Piper submit call with affiliate tracking
 * @param {string} account - Chili Piper account name
 * @param {string} router - Chili Piper router name
 * @param {object} options - Chili Piper options
 * @returns {object} Updated options with affiliate tracking
 */
export function addAffiliateTrackingToChiliPiperSubmit(account, router, options = {}) {
  const clickId = getIrClickId();
  
  if (!clickId) {
    console.log('ChiliPiper affiliate tracking: No irclickid cookie found');
    return options;
  }

  // Add affiliate tracking to the options
  // Chili Piper expects these as data fields, not lead properties
  const updatedOptions = {
    ...options,
    lead: {
      ...options.lead,
      // Add affiliate tracking as data fields for Chili Piper
      Platform: 'IMPACT',
      ClickId: clickId
    },
    // Also add as custom fields if Chili Piper supports them
    customFields: {
      ...options.customFields,
      Platform: 'IMPACT',
      ClickId: clickId
    }
  };

  console.log('ChiliPiper affiliate tracking: Added tracking to submit call', {
    platform: 'IMPACT',
    click_id: clickId
  });

  return updatedOptions;
}

/**
 * Enhanced Chili Piper submit function with affiliate tracking
 * @param {string} account - Chili Piper account name
 * @param {string} router - Chili Piper router name
 * @param {object} options - Chili Piper options
 */
export function submitChiliPiperWithTracking(account, router, options = {}) {
  if (window.ChiliPiper) {
    try {
      const trackedOptions = addAffiliateTrackingToChiliPiperSubmit(account, router, options);
      
      // Set up a listener to populate hidden fields when Chili Piper form loads
      const clickId = getIrClickId();
      if (clickId) {
        // Wait for Chili Piper form to load and populate hidden fields
        setTimeout(() => {
          const clickIdField = document.querySelector('input[name="ClickId"]');
          const platformField = document.querySelector('input[name="Platform"]');
          
          if (clickIdField) {
            clickIdField.value = clickId;
            console.log('ChiliPiper: Set ClickId to', clickId);
          }
          
          if (platformField) {
            platformField.value = 'IMPACT';
            console.log('ChiliPiper: Set Platform to IMPACT');
          }
        }, 1000); // Wait 1 second for form to load
      }
      
      window.ChiliPiper.submit(account, router, trackedOptions);
    } catch (error) {
      console.error('Error calling ChiliPiper with tracking:', error);
      // Fallback to original call without tracking
      window.ChiliPiper.submit(account, router, options);
    }
  } else {
    console.warn('ChiliPiper is not loaded yet');
  }
}

/**
 * Update all Chili Piper links on the page with affiliate tracking
 * @param {string} selector - CSS selector for Chili Piper links (default: '[href*="chilipiper.com"]')
 */
export function updateChiliPiperLinks(selector = '[href*="chilipiper.com"]') {
  const links = document.querySelectorAll(selector);
  
  links.forEach(link => {
    const originalHref = link.getAttribute('href');
    if (originalHref && originalHref.includes('chilipiper.com')) {
      const updatedHref = addAffiliateTrackingToUrl(originalHref);
      link.setAttribute('href', updatedHref);
      
      // Also update onclick handlers if they exist
      if (link.onclick) {
        const originalOnClick = link.onclick;
        link.onclick = function(e) {
          // Update href before the original onclick executes
          this.setAttribute('href', addAffiliateTrackingToUrl(originalHref));
          return originalOnClick.call(this, e);
        };
      }
    }
  });
}

/**
 * Create a Chili Piper link with affiliate tracking
 * @param {string} baseUrl - The base Chili Piper URL
 * @param {string} text - Link text
 * @param {string} className - CSS class name
 * @returns {HTMLAnchorElement} The link element
 */
export function createChiliPiperLink(baseUrl, text, className = '') {
  const link = document.createElement('a');
  link.href = addAffiliateTrackingToUrl(baseUrl);
  link.textContent = text;
  link.className = className;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  
  return link;
}

/**
 * Initialize affiliate tracking for Chili Piper
 * This should be called when the app loads
 */
export function initializeChiliPiperAffiliateTracking() {
  // Update existing links when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      updateChiliPiperLinks();
    });
  } else {
    updateChiliPiperLinks();
  }

  // Also update links when new content is loaded (for SPAs)
  if (typeof window.MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          updateChiliPiperLinks();
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Auto-initialize when this module is imported
if (typeof window !== 'undefined') {
  initializeChiliPiperAffiliateTracking();
} 